import type { Dispatch, SetStateAction } from 'react'
import React, { useEffect, useState } from 'react'

import { CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'

import type { Groups } from 'api-client'

export const TypeGroup = {
  DateType: 'date_type',
  ViewDateType: 'view_date_type',
  ViewUnitType: 'view_unit_type',
  SearchTargetEnabled: 'search_target_enabled',
  OrderUnitType: 'order_unit_type',
  OptimizeRange: 'optimize_range',
  OrderQuantityReason: 'order_quantity_reason',
  AuthType: 'auth_type',
  BusinessCategory: 'business_category',
  MinLoadCapacity: 'min_load_capacity',
  OptimizeLoadFactorLogic: 'optimize_load_factor_logic',
  search_date_range_start: 'search_date_range_start',
  search_date_range_start_short: 'search_date_range_start_short',
  search_date_range_end: 'search_date_range_end',
} as const

interface SelectTypeCodeProps {
  typeGroup: keyof typeof TypeGroup
  title?: string
  typeCode?: string | null | undefined
  setTypeCode: Dispatch<SetStateAction<string | null | undefined>>
  setTypeCodeInfo: Dispatch<SetStateAction<Groups | undefined>>
  setIsValid?: Dispatch<SetStateAction<boolean>>
  isRequired?: boolean
  errorMessage?: string
  selcetRequired?: boolean
}

function SelectTypeCode({
  typeGroup,
  title = '',
  typeCode,
  setTypeCode,
  setTypeCodeInfo,
  setIsValid,
  isRequired,
  errorMessage = '',
  selcetRequired = true
}: SelectTypeCodeProps) {
  const { typeCode: typeCodes } = useAuthContext()

  const [trTypecode, setTrTypecode] = useState<Groups | undefined>(undefined)
  const [trValue, setTrValue] = useState<string | undefined | null>('')

  useEffect(() => {
    if (typeCodes.length > 0) {
      const findTypeCode = _.find(typeCodes, { typeGroup: TypeGroup[typeGroup] })
      setTrTypecode(findTypeCode)
      setTypeCodeInfo(findTypeCode)

      const defaultCode = _.find(findTypeCode?.codes, { defaultFlg: true })?.typeCode
        ?? _.find(findTypeCode?.codes)?.typeCode

      if (typeCode) {
        setTrValue(typeCode)
        setTypeCode(typeCode)
      } else if (isRequired) {
        setTrValue(defaultCode)
        setTypeCode(defaultCode)
      } else {
        setTrValue('')
        setTypeCode('')
      }
    }
  }, [typeCodes])

  // Keep local select value in sync when parent updates typeCode (e.g. restore from localStorage)
  useEffect(() => {
    if (typeCode === undefined) return
    setTrValue(typeCode ?? '')
  }, [typeCode])

  useEffect(() => {
    if (isRequired) {
      setIsValid?.(!!trValue && trValue.length > 0)
    } else {
      setIsValid?.(true)
    }
  }, [trValue, isRequired])

   useEffect(() => {
    if(typeCode === undefined) {
      setTrValue('')
    }
  }, [typeCode])

  return trTypecode ? (
    <FormControl
      sx={{ minWidth: 120 }}
      error={isRequired ? trValue === '' : false}
      required={selcetRequired ? isRequired : false}
    >
      <InputLabel id="select-type-code">{title || trTypecode.typeGroupName}</InputLabel>
      <Select
        labelId="select-type-code"
        id="demo-simple-select"
        value={trValue ?? ''}
        onChange={(e) => {
          const selectedValue = e.target.value
          setTrValue(selectedValue)
          setTypeCode(selectedValue)
        }}
        required={selcetRequired ? isRequired : false}
      >
        {trTypecode.codes.map((value) => (
          <MenuItem key={value.sortOrder} value={value.typeCode}>
            {value.typeName}
          </MenuItem>
        ))}
      </Select>
      {isRequired && trValue === '' && (
        <FormHelperText>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  ) : (
    <FormControl>
      <InputLabel id="select-type-code" />
      <CircularProgress color="inherit" size={30} />
    </FormControl>
  )
}

export default SelectTypeCode
